import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import {
  PackageOpen, Plus, Settings, Loader2, CheckCircle2, XCircle, Clock, RefreshCw,
  Eye, Edit, Trash2, FileSpreadsheet, AlertTriangle, History,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${API_URL}/api/salida-equipos`;

const TIPOS = ["Laptop", "PC", "Proyector"];
const hoy = () => new Date().toISOString().slice(0, 10);

const ESTADOS = {
  pendiente_aprobacion: { label: "Pendiente Aprobación", cls: "bg-amber-500 hover:bg-amber-500", Icon: Clock },
  aprobada: { label: "Aprobada", cls: "bg-emerald-600 hover:bg-emerald-600", Icon: CheckCircle2 },
  declinada: { label: "Declinada", cls: "bg-red-600 hover:bg-red-600", Icon: XCircle },
};

const fdmy = (s) => {
  if (!s) return "—";
  const [y, m, d] = s.split("-");
  return d && m && y ? `${d}/${m}/${y}` : s;
};

export default function SalidaEquiposPage() {
  const { user, getAuthHeaders } = useAuth();
  const isAdmin = ["admin", "administrador", "superadmin"].includes((user?.role || "").toLowerCase());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openNuevo, setOpenNuevo] = useState(false);
  const [openConfig, setOpenConfig] = useState(false);
  const [accion, setAccion] = useState(null); // {solicitud, tipo: "aprobar"|"declinar"}
  const [verItem, setVerItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [regresandoId, setRegresandoId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [openHist, setOpenHist] = useState(false);
  const [fEstatus, setFEstatus] = useState("todos");
  const [fTipo, setFTipo] = useState("todos");
  const [fDep, setFDep] = useState("todos");
  const [fSolicitante, setFSolicitante] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/solicitudes`, getAuthHeaders());
      setItems(r.data.items || []);
    } catch (e) { toast.error("Error al cargar solicitudes"); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const marcarRegreso = async (it) => {
    if (!window.confirm("¿Confirma que el equipo ya regresó a la oficina? Esta marcación no se puede deshacer.")) return;
    setRegresandoId(it.id);
    try {
      await axios.post(`${API}/solicitudes/${it.id}/regreso`, {}, getAuthHeaders());
      toast.success("Regreso del equipo registrado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al registrar el regreso"); }
    finally { setRegresandoId(null); }
  };

  const eliminar = async (it) => {
    if (!window.confirm(`¿Eliminar la solicitud #${it.numero} de ${it.solicitante_nombre}? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/solicitudes/${it.id}`, getAuthHeaders());
      toast.success("Solicitud eliminada");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al eliminar"); }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/solicitudes/export/excel`, { ...getAuthHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `salida_equipos_${hoy()}.xlsx`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error al exportar"); }
    finally { setExporting(false); }
  };

  const departamentos = [...new Set(items.map(i => i.departamento).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const filtered = items.filter(it =>
    (fEstatus === "todos" || it.estado === fEstatus) &&
    (fTipo === "todos" || it.tipo_equipo === fTipo) &&
    (fDep === "todos" || (it.departamento || "") === fDep) &&
    (!fSolicitante.trim() || (it.solicitante_nombre || "").toLowerCase().includes(fSolicitante.trim().toLowerCase()))
  );

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      <Sidebar />
      <main className="flex-1 min-w-0 ml-0 md:ml-64 p-4 md:p-6 overflow-x-clip" data-testid="salida-equipos-page">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-6 rounded-2xl bg-gradient-to-r from-teal-600 via-teal-500 to-emerald-500 p-5 shadow-lg">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-12 h-12 shrink-0 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
              <PackageOpen className="w-7 h-7 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl md:text-2xl font-bold text-white">Solicitud Salida de Equipos</h1>
              <p className="text-sm text-white/85">Solicita y aprueba la salida de equipos de la oficina</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isAdmin && (
              <Button variant="secondary" onClick={() => setOpenConfig(true)} data-testid="btn-config-salida">
                <Settings className="w-4 h-4 mr-1" /> Configuración
              </Button>
            )}
            <Button className="bg-white text-teal-700 hover:bg-white/90" onClick={() => setOpenNuevo(true)} data-testid="btn-nuevo-registro">
              <Plus className="w-4 h-4 mr-1" /> Nuevo Registro
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 max-w-4xl" data-testid="salida-contadores">
          <Card className="border-l-4 border-l-amber-500 shadow-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-amber-100 dark:bg-amber-950/50 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-800 dark:text-slate-100" data-testid="contador-pendientes">
                  {items.filter(i => i.estado === "pendiente_aprobacion").length}
                </p>
                <p className="text-sm text-slate-500">Pendientes de Aprobar</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-600 shadow-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-emerald-100 dark:bg-emerald-950/50 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-800 dark:text-slate-100" data-testid="contador-aprobadas">
                  {items.filter(i => i.estado === "aprobada").length}
                </p>
                <p className="text-sm text-slate-500">Aprobadas</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-600 shadow-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-red-100 dark:bg-red-950/50 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-800 dark:text-slate-100" data-testid="contador-atrasados">
                  {items.filter(i => i.atrasado).length}
                </p>
                <p className="text-sm text-slate-500">Atrasados sin Devolver</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Solicitudes</CardTitle>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setOpenHist(true)} disabled={items.length === 0} data-testid="btn-historial-equipos">
                <History className="w-4 h-4 mr-1 text-indigo-600" /> Historial de Equipos
              </Button>
              <Button size="sm" variant="outline" onClick={exportExcel} disabled={exporting || items.length === 0} data-testid="btn-exportar-excel">
                {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1 text-emerald-600" />} Exportar Excel
              </Button>
              <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="btn-refrescar-solicitudes">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="salida-filtros">
              <div>
                <Label className="text-xs text-slate-500">Estatus</Label>
                <Select value={fEstatus} onValueChange={setFEstatus}>
                  <SelectTrigger className="w-44" data-testid="filtro-estatus"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="pendiente_aprobacion">Pendiente Aprobación</SelectItem>
                    <SelectItem value="aprobada">Aprobada</SelectItem>
                    <SelectItem value="declinada">Declinada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Tipo de equipo</Label>
                <Select value={fTipo} onValueChange={setFTipo}>
                  <SelectTrigger className="w-36" data-testid="filtro-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {TIPOS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Departamento</Label>
                <Select value={fDep} onValueChange={setFDep}>
                  <SelectTrigger className="w-48" data-testid="filtro-departamento"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {departamentos.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Solicitante</Label>
                <Input className="w-48" placeholder="Buscar por nombre..." value={fSolicitante}
                  onChange={(e) => setFSolicitante(e.target.value)} data-testid="filtro-solicitante" />
              </div>
              {(fEstatus !== "todos" || fTipo !== "todos" || fDep !== "todos" || fSolicitante) && (
                <Button size="sm" variant="ghost" className="text-slate-500"
                  onClick={() => { setFEstatus("todos"); setFTipo("todos"); setFDep("todos"); setFSolicitante(""); }}
                  data-testid="filtro-limpiar">
                  <XCircle className="w-4 h-4 mr-1" /> Limpiar filtros
                </Button>
              )}
            </div>
            {items.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center" data-testid="salida-sin-solicitudes">
                No hay solicitudes registradas. Use "Nuevo Registro" para crear una.
              </p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center" data-testid="salida-sin-resultados">
                No hay solicitudes que coincidan con los filtros.
              </p>
            ) : (
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <Table data-testid="salida-solicitudes-table">
                    <TableHeader>
                      <TableRow className="bg-[#0C314C] hover:bg-[#0C314C]">
                        <TableHead className="text-white font-semibold">#</TableHead>
                        <TableHead className="text-white font-semibold">Solicitante</TableHead>
                        <TableHead className="text-white font-semibold">Departamento</TableHead>
                        <TableHead className="text-white font-semibold">Equipo</TableHead>
                        <TableHead className="text-white font-semibold">Serie</TableHead>
                        <TableHead className="text-white font-semibold">Accesorios</TableHead>
                        <TableHead className="text-white font-semibold">F. Solicitud</TableHead>
                        <TableHead className="text-white font-semibold">F. Regreso</TableHead>
                        <TableHead className="text-white font-semibold">Motivo</TableHead>
                        <TableHead className="text-white font-semibold">Estatus</TableHead>
                        <TableHead className="text-white font-semibold">Comentario</TableHead>
                        <TableHead className="text-white font-semibold">Regreso</TableHead>
                        <TableHead className="text-white font-semibold text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((it, idx) => {
                        const est = ESTADOS[it.estado] || ESTADOS.pendiente_aprobacion;
                        const EIcon = est.Icon;
                        return (
                          <TableRow key={it.id}
                            className={`${idx % 2 === 1 ? "bg-slate-50/70 dark:bg-slate-800/40" : ""} hover:bg-teal-50/60 dark:hover:bg-teal-950/30 transition-colors`}
                            data-testid={`salida-row-${it.id}`}>
                            <TableCell className="font-semibold text-slate-700 dark:text-slate-200">{it.numero}</TableCell>
                            <TableCell>
                              <div className="font-medium">{it.solicitante_nombre}</div>
                            </TableCell>
                            <TableCell className="text-slate-600 dark:text-slate-300">{it.departamento || "—"}</TableCell>
                            <TableCell>
                              <div className="font-medium">{it.tipo_equipo}</div>
                              <div className="text-xs text-slate-500">{it.marca} {it.modelo}</div>
                            </TableCell>
                            <TableCell className="text-slate-600 dark:text-slate-300">{it.serie}</TableCell>
                            <TableCell className="max-w-[140px] truncate" title={it.accesorios}>{it.accesorios || "—"}</TableCell>
                            <TableCell className="whitespace-nowrap">{fdmy(it.fecha_solicitud)}</TableCell>
                            <TableCell className="whitespace-nowrap">{fdmy(it.fecha_regreso)}</TableCell>
                            <TableCell className="max-w-[180px] truncate" title={it.motivo}>{it.motivo}</TableCell>
                            <TableCell>
                              <Badge className={est.cls} data-testid={`salida-estado-${it.id}`}><EIcon className="w-3 h-3 mr-1" /> {est.label}</Badge>
                              {it.accion_por && <div className="text-xs text-slate-500 mt-0.5">por {it.accion_por}</div>}
                            </TableCell>
                            <TableCell className="max-w-[180px] truncate" title={it.comentario}>{it.comentario || "—"}</TableCell>
                            <TableCell className="whitespace-nowrap">
                              {it.regresado ? (
                                <div data-testid={`salida-regresado-${it.id}`}>
                                  <Badge className="bg-teal-600 hover:bg-teal-600"><CheckCircle2 className="w-3 h-3 mr-1" /> Regresado</Badge>
                                  <div className="text-xs text-slate-500 mt-0.5">{fdmy(it.fecha_regreso_real)} · {it.regreso_por}</div>
                                </div>
                              ) : it.puede_marcar_regreso ? (
                                <div>
                                  {it.atrasado && <Badge className="bg-red-600 hover:bg-red-600 mb-1" data-testid={`salida-atrasado-${it.id}`}>Atrasado</Badge>}
                                  <Button size="sm" variant="outline" className="block border-teal-500 text-teal-700 hover:bg-teal-50"
                                    onClick={() => marcarRegreso(it)} disabled={regresandoId === it.id} data-testid={`btn-marcar-regreso-${it.id}`}>
                                    {regresandoId === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Marcar regreso"}
                                  </Button>
                                </div>
                              ) : it.estado === "aprobada" ? (
                                it.atrasado
                                  ? <Badge className="bg-red-600 hover:bg-red-600" data-testid={`salida-atrasado-${it.id}`}>Atrasado</Badge>
                                  : <span className="text-xs text-slate-400">Pendiente</span>
                              ) : "—"}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              {it.puede_aprobar && (
                                <>
                                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 mr-1"
                                    onClick={() => setAccion({ solicitud: it, tipo: "aprobar" })} data-testid={`btn-aprobar-${it.id}`}>
                                    <CheckCircle2 className="w-4 h-4 mr-1" /> Aprobar
                                  </Button>
                                  <Button size="sm" variant="destructive" className="mr-1"
                                    onClick={() => setAccion({ solicitud: it, tipo: "declinar" })} data-testid={`btn-declinar-${it.id}`}>
                                    <XCircle className="w-4 h-4 mr-1" /> Declinar
                                  </Button>
                                </>
                              )}
                              {isAdmin && (
                                <>
                                  <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-teal-700"
                                    title="Ver detalle" onClick={() => setVerItem(it)} data-testid={`btn-ver-${it.id}`}>
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-indigo-600"
                                    title="Editar" onClick={() => setEditItem(it)} data-testid={`btn-editar-${it.id}`}>
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-red-600"
                                    title="Eliminar" onClick={() => eliminar(it)} data-testid={`btn-eliminar-${it.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <RegistroDialog open={openNuevo || !!editItem} onClose={() => { setOpenNuevo(false); setEditItem(null); }}
          user={user} getAuthHeaders={getAuthHeaders} onSaved={load} editItem={editItem} />
        <AccionDialog accion={accion} setAccion={setAccion} getAuthHeaders={getAuthHeaders} onDone={load} />
        <VerDialog item={verItem} onClose={() => setVerItem(null)} />
        <HistorialEquiposDialog open={openHist} onClose={() => setOpenHist(false)} items={items} />
        {isAdmin && <ConfigDialog open={openConfig} setOpen={setOpenConfig} getAuthHeaders={getAuthHeaders} />}
      </main>
    </div>
  );
}

function RegistroDialog({ open, onClose, user, getAuthHeaders, onSaved, editItem }) {
  const empty = { tipo_equipo: "", marca: "", modelo: "", serie: "", accesorios: "", fecha_solicitud: hoy(), fecha_regreso: "", motivo: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editItem) {
      setForm({
        tipo_equipo: editItem.tipo_equipo || "", marca: editItem.marca || "", modelo: editItem.modelo || "",
        serie: editItem.serie || "", accesorios: editItem.accesorios || "",
        fecha_solicitud: editItem.fecha_solicitud || hoy(), fecha_regreso: editItem.fecha_regreso || "",
        motivo: editItem.motivo || "",
      });
    } else {
      setForm({ ...empty, fecha_solicitud: hoy() });
    }
  }, [open, editItem]); // eslint-disable-line

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async () => {
    if (!form.tipo_equipo) { toast.error("Seleccione el tipo de equipo"); return; }
    for (const [k, label] of [["marca", "Marca"], ["modelo", "Modelo"], ["serie", "Serie"], ["fecha_regreso", "Fecha de regreso"], ["motivo", "Motivo de salida"]]) {
      if (!(form[k] || "").trim()) { toast.error(`El campo '${label}' es obligatorio`); return; }
    }
    setSaving(true);
    try {
      if (editItem) {
        await axios.put(`${API}/solicitudes/${editItem.id}`, form, getAuthHeaders());
        toast.success("Solicitud actualizada");
      } else {
        const r = await axios.post(`${API}/solicitudes`, form, getAuthHeaders());
        if (r.data?.item?.estado === "aprobada") toast.success("Solicitud creada y aprobada automáticamente. Se notificó a Tecnología.");
        else toast.success("Solicitud creada. Se notificó al supervisor para su aprobación.");
      }
      onClose(); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar la solicitud"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="salida-nuevo-dialog">
        <DialogHeader>
          <DialogTitle>{editItem ? `Editar Solicitud #${editItem.numero}` : "Nueva Solicitud de Salida de Equipo"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nombre Solicitante</Label>
            <Input value={editItem ? editItem.solicitante_nombre : (user?.name || user?.nombre || "")} disabled data-testid="salida-input-solicitante" />
          </div>
          <div>
            <Label>Tipo de Equipo *</Label>
            <Select value={form.tipo_equipo} onValueChange={set("tipo_equipo")}>
              <SelectTrigger data-testid="salida-select-tipo"><SelectValue placeholder="Seleccione..." /></SelectTrigger>
              <SelectContent>
                {TIPOS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Marca *</Label>
              <Input value={form.marca} onChange={set("marca")} data-testid="salida-input-marca" />
            </div>
            <div>
              <Label>Modelo *</Label>
              <Input value={form.modelo} onChange={set("modelo")} data-testid="salida-input-modelo" />
            </div>
          </div>
          <div>
            <Label>Serie *</Label>
            <Input value={form.serie} onChange={set("serie")} data-testid="salida-input-serie" />
          </div>
          <div>
            <Label>Accesorios a retirar</Label>
            <Input value={form.accesorios} onChange={set("accesorios")} placeholder="Cargador, mouse, maletín..." data-testid="salida-input-accesorios" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Fecha de Solicitud</Label>
              <Input type="date" value={form.fecha_solicitud} disabled data-testid="salida-input-fecha-solicitud" />
            </div>
            <div>
              <Label>Fecha de Regreso *</Label>
              <Input type="date" value={form.fecha_regreso} min={form.fecha_solicitud} onChange={set("fecha_regreso")} data-testid="salida-input-fecha-regreso" />
            </div>
          </div>
          <div>
            <Label>Motivo de Salida *</Label>
            <Textarea rows={3} value={form.motivo} onChange={set("motivo")} data-testid="salida-input-motivo" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="salida-btn-solicitar">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} {editItem ? "Guardar cambios" : "Solicitar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VerDialog({ item, onClose }) {
  if (!item) return null;
  const est = ESTADOS[item.estado] || ESTADOS.pendiente_aprobacion;
  const rows = [
    ["Solicitante", `${item.solicitante_nombre}${item.departamento ? ` (${item.departamento})` : ""}`],
    ["Tipo de equipo", item.tipo_equipo],
    ["Marca / Modelo", `${item.marca} ${item.modelo}`],
    ["Serie", item.serie],
    ["Accesorios a retirar", item.accesorios || "Ninguno"],
    ["Fecha de solicitud", fdmy(item.fecha_solicitud)],
    ["Fecha de regreso", fdmy(item.fecha_regreso)],
    ["Motivo de salida", item.motivo],
    ["Supervisor", item.supervisor_nombre || "—"],
    ["Estatus", est.label],
    ["Comentario", item.comentario || "—"],
    ["Procesada por", item.accion_por || "—"],
    ["Regreso", item.regresado ? `Sí — ${fdmy(item.fecha_regreso_real)} (${item.regreso_por})` : (item.estado === "aprobada" ? "Pendiente" : "—")],
  ];
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="salida-ver-dialog">
        <DialogHeader><DialogTitle>Solicitud #{item.numero}</DialogTitle></DialogHeader>
        <div className="rounded-lg border divide-y text-sm">
          {rows.map(([k, v]) => (
            <div key={k} className="flex px-3 py-2 gap-3">
              <div className="w-40 shrink-0 text-slate-500">{k}</div>
              <div className="font-medium">{v}</div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistorialEquiposDialog({ open, onClose, items }) {
  const [serieSel, setSerieSel] = useState(null);
  useEffect(() => { if (!open) setSerieSel(null); }, [open]);
  const norm = (s) => (s || "").trim().toUpperCase() || "SIN SERIE";
  const grupos = {};
  items.filter(i => i.estado === "aprobada").forEach(i => {
    const key = norm(i.serie);
    if (!grupos[key]) grupos[key] = { key, serie: i.serie || "Sin serie", equipos: new Set(), salidas: 0, ultima: "", pendiente: false };
    const g = grupos[key];
    g.salidas += 1;
    g.equipos.add(`${i.tipo_equipo} ${i.marca} ${i.modelo}`.trim());
    if ((i.fecha_solicitud || "") > g.ultima) g.ultima = i.fecha_solicitud || "";
    if (!i.regresado) g.pendiente = true;
  });
  const lista = Object.values(grupos).sort((a, b) => b.salidas - a.salidas || a.serie.localeCompare(b.serie));
  const detalle = serieSel
    ? items.filter(i => i.estado === "aprobada" && norm(i.serie) === serieSel.key)
        .sort((a, b) => (b.fecha_solicitud || "").localeCompare(a.fecha_solicitud || ""))
    : [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="salida-historial-dialog">
        <DialogHeader>
          <DialogTitle>
            {serieSel ? `Salidas del equipo — Serie ${serieSel.serie}` : "Historial de Equipos — Salidas por Serie"}
          </DialogTitle>
        </DialogHeader>
        {serieSel ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-500">{[...serieSel.equipos].join(" / ")} · {detalle.length} salida(s)</p>
            <div className="rounded-lg border overflow-hidden">
              <Table data-testid="historial-detalle-table">
                <TableHeader>
                  <TableRow className="bg-[#0C314C] hover:bg-[#0C314C]">
                    <TableHead className="text-white font-semibold">#</TableHead>
                    <TableHead className="text-white font-semibold">Solicitante</TableHead>
                    <TableHead className="text-white font-semibold">F. Salida</TableHead>
                    <TableHead className="text-white font-semibold">F. Regreso Acordada</TableHead>
                    <TableHead className="text-white font-semibold">Devuelto</TableHead>
                    <TableHead className="text-white font-semibold">Motivo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detalle.map((i, idx) => (
                    <TableRow key={i.id} className={idx % 2 === 1 ? "bg-slate-50/70 dark:bg-slate-800/40" : ""} data-testid={`detalle-serie-row-${i.id}`}>
                      <TableCell className="font-medium">{i.numero}</TableCell>
                      <TableCell>
                        <div className="font-medium">{i.solicitante_nombre}</div>
                        <div className="text-xs text-slate-500">{i.departamento || "—"}</div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{fdmy(i.fecha_solicitud)}</TableCell>
                      <TableCell className="whitespace-nowrap">{fdmy(i.fecha_regreso)}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        {i.regresado
                          ? <Badge className="bg-teal-600 hover:bg-teal-600">{fdmy(i.fecha_regreso_real)}</Badge>
                          : (i.atrasado
                            ? <Badge className="bg-red-600 hover:bg-red-600">Atrasado</Badge>
                            : <Badge className="bg-amber-500 hover:bg-amber-500">Fuera de oficina</Badge>)}
                      </TableCell>
                      <TableCell className="max-w-[180px] truncate" title={i.motivo}>{i.motivo}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : lista.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center" data-testid="historial-vacio">Aún no hay salidas aprobadas de equipos.</p>
        ) : (
          <div className="rounded-lg border overflow-hidden">
            <Table data-testid="historial-equipos-table">
              <TableHeader>
                <TableRow className="bg-[#0C314C] hover:bg-[#0C314C]">
                  <TableHead className="text-white font-semibold">Serie</TableHead>
                  <TableHead className="text-white font-semibold">Equipo</TableHead>
                  <TableHead className="text-white font-semibold text-center">Veces que ha salido</TableHead>
                  <TableHead className="text-white font-semibold">Última salida</TableHead>
                  <TableHead className="text-white font-semibold">Situación</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lista.map((g, idx) => (
                  <TableRow key={g.serie} onClick={() => setSerieSel(g)}
                    className={`${idx % 2 === 1 ? "bg-slate-50/70 dark:bg-slate-800/40" : ""} cursor-pointer hover:bg-teal-50/60 dark:hover:bg-teal-950/30`}
                    title="Clic para ver todas las salidas de esta serie" data-testid={`historial-row-${idx}`}>
                    <TableCell className="font-medium text-teal-700 dark:text-teal-400 underline underline-offset-2">{g.serie}</TableCell>
                    <TableCell className="text-slate-600 dark:text-slate-300">{[...g.equipos].join(" / ")}</TableCell>
                    <TableCell className="text-center">
                      <Badge className="bg-indigo-600 hover:bg-indigo-600">{g.salidas}</Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{fdmy(g.ultima)}</TableCell>
                    <TableCell>
                      {g.pendiente
                        ? <Badge className="bg-amber-500 hover:bg-amber-500">Fuera de oficina</Badge>
                        : <Badge className="bg-teal-600 hover:bg-teal-600">En oficina</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <DialogFooter>
          {serieSel && (
            <Button variant="outline" onClick={() => setSerieSel(null)} data-testid="btn-volver-historial">← Volver al historial</Button>
          )}
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AccionDialog({ accion, setAccion, getAuthHeaders, onDone }) {
  const [comentario, setComentario] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (accion) setComentario(""); }, [accion]);
  if (!accion) return null;
  const { solicitud, tipo } = accion;
  const aprobar = tipo === "aprobar";

  const ejecutar = async () => {
    if (!comentario.trim()) { toast.error("El comentario es obligatorio"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/solicitudes/${solicitud.id}/${tipo}`, { comentario }, getAuthHeaders());
      toast.success(aprobar ? "Solicitud aprobada. Se notificó a Tecnología." : "Solicitud declinada. Se notificó al solicitante.");
      setAccion(null); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al procesar"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!accion} onOpenChange={(v) => !v && setAccion(null)}>
      <DialogContent className="max-w-md" data-testid="salida-accion-dialog">
        <DialogHeader>
          <DialogTitle>{aprobar ? "Aprobar" : "Declinar"} Solicitud #{solicitud.numero}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-3 text-sm">
            <p><strong>{solicitud.solicitante_nombre}</strong> — {solicitud.tipo_equipo} {solicitud.marca} {solicitud.modelo}</p>
            <p className="text-xs text-slate-500 mt-1">Serie: {solicitud.serie} · Regreso: {fdmy(solicitud.fecha_regreso)}</p>
            <p className="text-xs text-slate-500">Motivo: {solicitud.motivo}</p>
          </div>
          <div>
            <Label>Comentario (obligatorio) *</Label>
            <Textarea rows={3} value={comentario} onChange={(e) => setComentario(e.target.value)}
              placeholder={aprobar ? "Ej: Aprobado para visita a cliente" : "Indique por qué se declina la solicitud"}
              data-testid="salida-input-comentario" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setAccion(null)}>Cancelar</Button>
          <Button onClick={ejecutar} disabled={saving}
            className={aprobar ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
            data-testid="salida-btn-confirmar-accion">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} {aprobar ? "Aprobar" : "Declinar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfigDialog({ open, setOpen, getAuthHeaders }) {
  const [cfg, setCfg] = useState({ hora_maxima: "16:00", email_tecnologia: "", dias_atraso_copia_supervisor: 3 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/config`, getAuthHeaders())
      .then(r => setCfg(r.data))
      .catch(() => toast.error("Error al cargar configuración"));
  }, [open, getAuthHeaders]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/config`, cfg, getAuthHeaders());
      toast.success("Configuración guardada");
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="salida-config-dialog">
        <DialogHeader><DialogTitle>Configuración — Salida de Equipos</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Hora máxima para solicitar salida de equipos</Label>
            <Input type="time" value={cfg.hora_maxima} onChange={(e) => setCfg(c => ({ ...c, hora_maxima: e.target.value }))}
              className="w-36" data-testid="salida-config-hora" />
            <p className="text-xs text-slate-500 mt-1">Después de esta hora no se podrán crear solicitudes (no aplica a administradores).</p>
          </div>
          <div>
            <Label>Correo(s) a notificar cuando se aprueba (Tecnología)</Label>
            <Input value={cfg.email_tecnologia} onChange={(e) => setCfg(c => ({ ...c, email_tecnologia: e.target.value }))}
              placeholder="soporte@qasesores.com.do, otro@qasesores.com.do" data-testid="salida-config-email" />
            <p className="text-xs text-slate-500 mt-1">Puede colocar varios correos separados por coma.</p>
          </div>
          <div>
            <Label>Copiar al supervisor tras días de atraso</Label>
            <Input type="number" min={1} max={60} className="w-28" value={cfg.dias_atraso_copia_supervisor}
              onChange={(e) => setCfg(c => ({ ...c, dias_atraso_copia_supervisor: e.target.value }))}
              data-testid="salida-config-dias-copia" />
            <p className="text-xs text-slate-500 mt-1">Cuando un equipo atrasado alcance estos días sin devolverse, el recordatorio diario también se enviará al supervisor del solicitante.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="salida-config-guardar">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
